import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import { openChart } from '../components/ChartWindow';
import { wsUrl } from '../services/urls';

function Dashboard() {
  const [status, setStatus] = useState(null);
  const [trades, setTrades] = useState([]);
  const [pnl, setPnl] = useState({ daily_pnl: 0, total_pnl: 0, month_wise: [] });
  const [showMonths, setShowMonths] = useState(false);   // click Total P&L -> month-wise breakdown
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentMode, setCurrentMode] = useState('paper');
  const [currentSegment, setCurrentSegment] = useState('equity');
  const [paperBalance, setPaperBalance] = useState(100000);
  const [liveBalance, setLiveBalance] = useState(100000);

  const [stockCount, setStockCount] = useState(0);
  const [marketOpen, setMarketOpen] = useState(true);
  const [marketStatus, setMarketStatus] = useState('');
  const [currentBroker, setCurrentBroker] = useState('mstock');
  const [activeExchange, setActiveExchange] = useState('NSE');
  const [activityData, setActivityData] = useState(null);
  const [activityTab, setActivityTab] = useState('log');
  const [globalCtx, setGlobalCtx] = useState(null);
  const [showGlobal, setShowGlobal] = useState(false);
  // pending HOLD checkbox state per symbol (before Save) + last-saved flash
  const [holdPending, setHoldPending] = useState({});
  const [holdSaved, setHoldSaved] = useState({});
  const [brokerLogin, setBrokerLogin] = useState({ otpSent: false, otp: '', sending: false });
  const [equityProduct, setEquityProduct] = useState('MIS');
  const productTouchedAt = useRef(0);   // don't let a background refresh undo the user's just-made choice
  const [segAvail, setSegAvail] = useState(null); // per-broker segment availability

  // ROLE SPLIT: admin runs the engine; clients get a browsable VIEW — they can
  // flip Exchange/Type locally to see their own broker's availability, without
  // touching the shared engine (the server would 403 those writes anyway).
  const isAdmin = (() => {
    try { return (JSON.parse(localStorage.getItem('app_user') || '{}').role) === 'admin'; }
    catch { return false; }
  })();
  // CLOSE WITHOUT BROWSER POP-UPS (owner 2026-10-19: "not able to exit the position manually" - window.confirm is
  // blocked in iPhone home-screen apps / some browsers): tap Close -> 'Confirm close?' for 4 s -> tap again.
  const [armClose, setArmClose] = useState('');
  // EXIT WINDOW (owner 2026-10-23: "click Close -> a window: exit at Limit or Market, with the price")
  const [exitWin, setExitWin] = useState(null);   // { key, label, ltp, qty }
  const [exitType, setExitType] = useState('MARKET');
  const [exitPx, setExitPx] = useState('');
  const [exitBusy, setExitBusy] = useState(false);
  const [exitN, setExitN] = useState(1);   // lots to exit (units when the lot size is unknown)
  const openExit = (key, pos) => {
    const lot = +pos.lot_size > 0 && pos.quantity % pos.lot_size === 0 ? +pos.lot_size : 0;
    setExitWin({ key, label: pos.trade_symbol || key, ltp: +pos.current_price || 0, qty: pos.quantity, mode: pos.client_mode || '', lot });
    setExitN(lot ? Math.round(pos.quantity / lot) : pos.quantity);
    setExitType('MARKET'); setExitPx(pos.current_price ? String((+pos.current_price).toFixed(2)) : '');
    const had = pending.find(o => o.pos_key === key && o.kind === 'exit');   // already has a limit sell -> edit it
    if (had) {
      setExitType('LIMIT'); setExitPx(String(had.limit));
      if (lot && had.qty % lot === 0) setExitN(Math.round(had.qty / lot)); else if (!lot) setExitN(had.qty);
    }
  };
  const confirmExit = async () => {
    if (!exitWin) return;
    setExitBusy(true);
    try {
      let r;
      const units = Math.min(exitWin.qty, Math.max(1, (exitWin.lot || 1) * (parseInt(exitN, 10) || 0)));
      const part = units < exitWin.qty ? units : 0;          // 0 = the whole position
      if (exitType === 'LIMIT') {
        const px = parseFloat(exitPx);
        if (!(px > 0)) { setExitBusy(false); return; }
        const had = pending.find(o => o.pos_key === exitWin.key && o.kind === 'exit');
        r = had ? await api.modifyPendingOrder(had.id, px, part || exitWin.qty) : await api.limitExit(exitWin.key, px, part);
      } else {
        r = await api.closePosition(exitWin.key, part);
      }
      setCloseMsg({ t: r && r.status === 'success' ? 'success' : 'error', m: (r && r.message) || 'Exit sent' });
      setExitWin(null);
      fetchData();
    } catch (e) { setCloseMsg({ t: 'error', m: 'Exit failed — server not reachable' }); }
    setExitBusy(false);
    setTimeout(() => setCloseMsg(null), 8000);
  };
  // LIMIT EXIT + pending limit orders (owner 2026-10-23: "on the dashboard I can place the exit order by limit")
  const [pending, setPending] = useState([]);
  useEffect(() => {
    let alive = true;
    const q = async () => { try { const r = await api.getPendingOrders(); if (alive && r && r.status === 'success') setPending(r.orders || []); } catch (e) { /* keep last */ } };
    q(); const iv = setInterval(q, 4000);
    return () => { alive = false; clearInterval(iv); };
  }, []);
  const cancelPending = async (pid) => {
    try { const r = await api.cancelPendingOrder(pid); setCloseMsg({ t: r && r.status === 'success' ? 'success' : 'error', m: (r && r.message) || 'Cancelled' }); } catch (e) { /* ignore */ }
    setTimeout(() => setCloseMsg(null), 6000);
  };
  // EDIT A LIMIT ORDER (owner 2026-10-23: "i can also edit limit on all sides") - price + quantity in whole lots
  const [editPend, setEditPend] = useState(null);   // { id, px, n, lot, qty }
  const openEdit = (o) => {
    const lot = +o.lot > 0 && o.qty % o.lot === 0 ? +o.lot : 0;
    setEditPend({ id: o.id, px: String(o.limit), n: lot ? Math.round(o.qty / lot) : o.qty, lot });
  };
  const saveEdit = async () => {
    if (!editPend) return;
    const px = parseFloat(editPend.px);
    const units = Math.max(1, (editPend.lot || 1) * (parseInt(editPend.n, 10) || 0));
    if (!(px > 0)) return;
    try {
      const r = await api.modifyPendingOrder(editPend.id, px, units);
      setCloseMsg({ t: r && r.status === 'success' ? 'success' : 'error', m: (r && r.message) || 'Changed' });
      if (r && r.status === 'success') setEditPend(null);
      const q = await api.getPendingOrders(); if (q && q.status === 'success') setPending(q.orders || []);
    } catch (e) { setCloseMsg({ t: 'error', m: 'Edit failed — server not reachable' }); }
    setTimeout(() => setCloseMsg(null), 8000);
  };
  const [closeMsg, setCloseMsg] = useState(null);
  // WHAT YOUR DECISIONS TEACH (owner 2026-10-19) — approve / reject / manual decisions and their results
  const [dec, setDec] = useState(null);
  useEffect(() => {
    if (!isAdmin) return undefined;
    let alive = true;
    const load = async () => { try { const r = await api.getDecisionsReport(); if (alive && r && r.status === 'success') setDec(r); } catch (e) { /* keep last */ } };
    load(); const iv = setInterval(load, 120000);
    return () => { alive = false; clearInterval(iv); };
  }, [isAdmin]);
  // MISSED-TRADE TRACKER (owner 2026-10-15) — admin card; keeps the last good data (no flicker)
  const [miss, setMiss] = useState(null);
  const [missDays, setMissDays] = useState(1);
  useEffect(() => {
    if (!isAdmin) return undefined;
    let alive = true;
    const load = async () => { try { const r = await api.getMissedTrades(missDays); if (alive && r && r.status === 'success') setMiss(r); } catch (e) { /* keep last */ } };
    load(); const iv = setInterval(load, 60000);
    return () => { alive = false; clearInterval(iv); };
  }, [isAdmin, missDays]);
  // once a client picks an exchange/type to look at, server pushes stop overwriting it
  const clientViewTouched = useRef(false);
  // CLIENT Paper/Live toggle — now a REAL preference: the engine distributes
  // trades to this client per this setting (paper book / their broker / off)
  const [clientMode, setClientMode] = useState(null);
  const [optSell, setOptSell] = useState(false);
  // (iron condor / option selling REMOVED 2026-10-05 per user — no condor card or polling)
  // (AI CE+PE strategy ideas REMOVED 2026-10-13 per owner)
  const clientModeRef = useRef(null);
  const [modeChosen, setModeChosen] = useState(true);
  const [news, setNews] = useState([]);
  const [review, setReview] = useState(null);
  const [brain, setBrain] = useState(null);
  const [stockBg, setStockBg] = useState(null);   // background (virtual) stock-option trades, admin only
  const [stockBgView, setStockBgView] = useState('pass');

  // load saved trading pref (mode) — for CLIENTS and now the ADMIN too (the admin
  // trades like a client under its own account since the master was decommissioned)
  useEffect(() => {
    api.getMyTradingPrefs().then(r => {
      if (r.status === 'success') {
        setClientMode(r.trade_mode);
        setOptSell(!!r.allow_option_selling);
        clientModeRef.current = r.trade_mode;
        setModeChosen(!!r.mode_chosen);
        // restore their saved segment + exchange so the picker + filtering match
        if (Array.isArray(r.trade_segments) && r.trade_segments.length) {
          const seg = r.trade_segments[0];
          const exFor = { commodity: 'MCX', mcx_options: 'MCX', sensex: 'BSE' };
          clientViewTouched.current = true;
          setCurrentSegment(seg);
          setActiveExchange(exFor[seg] || 'NSE');
        }
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const load = () => api.getSegmentAvailability().then(setSegAvail).catch(() => {});
    load();
    const iv = setInterval(load, 10000);
    return () => clearInterval(iv);
  }, [currentBroker]);

  // 'available / not available on this broker' — honest, broker-driven labels
  const segInfo = (seg) => (segAvail?.segments || {})[seg] || null;
  const segLabel = (seg, base) => {
    const i = segInfo(seg);
    if (!i) return base;
    if (i.available && i.via && i.via !== segAvail?.broker) return `${base} (via ${i.via})`;
    if (!i.available) return `${base} — not available`;
    return base;
  };
  const segDisabled = (seg) => {
    const i = segInfo(seg);
    return i ? !i.available : false;
  };
  const audioCtxRef = useRef(null);
  const prevTradeCountRef = useRef(null);
  const fetchFailsRef = useRef(0);   // consecutive failed refreshes (no-flicker error banner)

  // Melodic notification sounds (not a flat tone): quick note patterns with a
  // soft bell timbre. Preference lives in Settings (localStorage 'tradeSound').
  const note = (freq, when, dur = 0.22, gain = 0.14) => {
    try {
      if (!audioCtxRef.current) audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      const ctx = audioCtxRef.current;
      const t0 = ctx.currentTime + when;
      [['triangle', freq, gain], ['sine', freq * 2, gain * 0.25]].forEach(([type, f, gn]) => {
        const o = ctx.createOscillator(); const g = ctx.createGain();
        o.type = type; o.frequency.value = f;
        g.gain.setValueAtTime(0.0001, t0);
        g.gain.linearRampToValueAtTime(gn, t0 + 0.012);
        g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
        o.connect(g); g.connect(ctx.destination);
        o.start(t0); o.stop(t0 + dur);
      });
    } catch (e) {}
  };
  const playTradeSound = (kind) => {
    if (localStorage.getItem('tradeSound') !== 'on') return;
    if (kind === 'open') {
      // quick "pop-ping" — like a message notification
      note(523, 0, 0.14); note(784, 0.11, 0.2);
    } else if (kind === 'win') {
      // bright major "ta-da-ding!"
      note(523, 0, 0.13); note(659, 0.1, 0.13); note(784, 0.2, 0.14); note(1046, 0.32, 0.34, 0.16);
    } else {
      // soft low "dun-dun" — noticeable, not harsh
      note(330, 0, 0.18, 0.13); note(247, 0.17, 0.3, 0.13);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      // NO FLICKER (user 2026-10-06): each request is independent — one slow/failed call keeps
      // the previous value on screen instead of throwing away the whole refresh.
      const _safe = (pr) => pr.catch(() => null);
      const [statusRes, pnlRes, tradesRes, modeRes, segmentRes, exchangeRes, brokerRes, activityRes] = await Promise.all([
        _safe(api.getStatus()),
        _safe(api.getPnL()),
        _safe(api.getTrades(clientModeRef.current)),  // client view-mode; null = engine mode
        _safe(api.getTradingMode()),
        _safe(api.getSegment()),
        _safe(api.getExchange()),
        _safe(api.getLiveBroker()),
        _safe(api.getActivity()),
      ]);
      if (!statusRes && !pnlRes && !modeRes) {          // the whole server didn't answer
        fetchFailsRef.current += 1;
        if (fetchFailsRef.current >= 3) setError('Server not responding — retrying…');
        setLoading(false);
        return;
      }
      fetchFailsRef.current = 0;
      setError(prev => (prev === 'Failed to fetch data' || prev === 'Server not responding — retrying…') ? '' : prev);
      api.getGlobalContext().then(r => { if (r?.status === 'ok') setGlobalCtx(r.context); }).catch(() => {});
      if (statusRes) setStatus(statusRes);
      // Backend P&L already includes unrealized (open positions) — use directly,
      // do NOT re-add unrealized here (that double-counted the open trade).
      if (pnlRes) setPnl({
        daily_pnl: pnlRes.daily_pnl || 0,
        total_pnl: pnlRes.total_pnl || 0,
      });
      // Trade sounds: any NEW trade event since the last poll — open (pnl absent)
      // clicks, profitable close rings up, losing close buzzes down
      const prevCount = prevTradeCountRef.current;
      if (prevCount !== null && Array.isArray(tradesRes) && tradesRes.length > prevCount) {
        tradesRes.slice(prevCount).forEach(t => {
          const isClose = t.pnl !== null && t.pnl !== undefined;
          playTradeSound(!isClose ? 'open' : (t.pnl >= 0 ? 'win' : 'loss'));
        });
      }
      if (Array.isArray(tradesRes)) prevTradeCountRef.current = tradesRes.length;
      if (Array.isArray(tradesRes)) setTrades(tradesRes);
      if (modeRes) {
        setCurrentMode(modeRes.mode);
        setPaperBalance(modeRes.paper_balance);
        setLiveBalance(modeRes.live_balance);
      }
      if (segmentRes && (isAdmin || !clientViewTouched.current)) {
        setCurrentSegment(segmentRes.segment);
        if (exchangeRes) setActiveExchange(exchangeRes.exchange || 'NSE');
      }
      if (segmentRes) {
        setStockCount(segmentRes.stock_count || 0);
        setMarketOpen(segmentRes.market_open !== false);
        setMarketStatus(segmentRes.market_status || '');
      }
      if (brokerRes) setCurrentBroker(brokerRes.broker || 'mstock');
      if (activityRes) setActivityData(activityRes);
      // ONE source: the user's own saved product (the server falls back to the global default).
      // Reading the global value too raced with it and kept flipping the box back to MIS.
      if (Date.now() - productTouchedAt.current > 8000) {
        api.getMyTradingPrefs().then(r => { if (r && r.equity_product) setEquityProduct(r.equity_product); }).catch(() => {});
      }
      setLoading(false);
    } catch (err) {
      fetchFailsRef.current += 1;
      if (fetchFailsRef.current >= 3) setError('Server not responding — retrying…');
      setLoading(false);
    }
  }, []);

  // Poll every 3 seconds (not 1s) to reduce race conditions with WebSocket
  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 2000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // LIVE MARKET NEWS — refresh every 30s (the backend caches 60s; free RSS is delayed
  // vs the market, so this is a news DISPLAY + context, not a beat-the-market signal).
  useEffect(() => {
    const loadNews = () => api.getNews().then(r => { if (r?.news) setNews(r.news); }).catch(() => {});
    loadNews();
    const iv = setInterval(loadNews, 5000);  // 5s refresh (user request)
    // Decision Review is ADMIN-ONLY (aggregate across all users) — never fetch it for a client.
    const loadReview = () => { if (!isAdmin) return; api.getDecisionReview().then(r => { if (r?.review && !r.review.error) setReview(r.review); }).catch(() => {}); };
    loadReview();
    const iv2 = setInterval(loadReview, 60000);  // decision review refresh (1 min)
    // Learning Brain (ADMIN-ONLY): live model AUC, nightly retrain + self-review, quarantine
    const loadBrain = () => { if (!isAdmin) return; api.getLearningBrain().then(r => { if (r?.brain) setBrain(r.brain); }).catch(() => {}); };
    loadBrain();
    const iv3 = setInterval(loadBrain, 60000);
    // INDEX-ONLY MODE (owner 2026-11-03): the stocks the background book 'traded' (virtual) - keeps the last good list
    const loadStockBg = () => { if (!isAdmin) return; api.getStockBackground().then(r => { if (r?.status === 'success') setStockBg(r); }).catch(() => {}); };
    loadStockBg();
    const iv4 = setInterval(loadStockBg, 15000);
    return () => { clearInterval(iv); clearInterval(iv2); clearInterval(iv3); clearInterval(iv4); };
  }, []);

  useEffect(() => {
    // token scopes the stream: clients receive THEIR data, not the admin's
    let ws = null, closed = false, retry = null;
    const connect = () => {
      if (closed) return;
      const wsToken = localStorage.getItem('app_token') || '';
      ws = new WebSocket(wsUrl(`/ws/live-data?token=${wsToken}`));
      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        // keep the alerts from the poll: this stream does not carry system_alerts, so replacing status
        // made the red "Action needed" box appear/disappear every second (owner 2026-10-14)
        setStatus(prev => (data.system_alerts === undefined && prev ? { ...data, system_alerts: prev.system_alerts } : data));
        setCurrentMode(data.current_mode);
        // BUGFIX 2026-08-18: do NOT set the segment/exchange from the WebSocket's GLOBAL
        // fields here — the scanner ROTATES them; the per-user stable values from
        // getSegment()/getExchange() (in fetchData) are the source of truth.
        if (data.paper_balance !== undefined) setPaperBalance(data.paper_balance);
        if (data.live_balance !== undefined) setLiveBalance(data.live_balance);
      };
      // AUTO-RECONNECT (BUGFIX 2026-08-19): without this, ANY drop — a network blip or a
      // backend restart — left the socket dead and LTP/positions FROZEN until a manual
      // page refresh ("LTP not updating in real time"). Reconnect 2s after a close.
      ws.onclose = () => { if (!closed) retry = setTimeout(connect, 2000); };
      ws.onerror = () => { try { ws.close(); } catch (e) {} };
    };
    connect();
    return () => { closed = true; if (retry) clearTimeout(retry); if (ws) { try { ws.close(); } catch (e) {} } };
  }, []);

  // Real-time price stream (pushed on every price change, no polling)
  useEffect(() => {
    let priceWs = null, closed = false, retry = null;
    const connect = () => {
    if (closed) return;
    const wsToken = localStorage.getItem('app_token') || '';
    priceWs = new WebSocket(wsUrl(`/ws/prices?token=${wsToken}`));
    priceWs.onmessage = (event) => {
      const msg = JSON.parse(event.data);
      if (msg.type === 'prices') {
        // Full position list from server (handles external closes)
        if (msg.positions) {
          // MERGE, never replace (2026-10-13 flicker): this stream carries a slim copy of each position;
          // replacing wiped the BTST / cash-leverage / swing / hold details 5x a second until the
          // live-data stream restored them. Keep every field we have, update what the stream sends,
          // and drop only positions that are really gone.
          setStatus(prev => {
            if (!prev) return prev;
            const old = prev.positions || {};
            const merged = {};
            Object.entries(msg.positions).forEach(([k, v]) => { merged[k] = old[k] ? { ...old[k], ...v } : v; });
            return { ...prev, positions: merged };
          });
        }
        // Price updates
        if (msg.data) {
          setStatus(prev => {
            if (!prev) return prev;
            const newPositions = { ...prev.positions };
            Object.entries(msg.data).forEach(([sym, p]) => {
              for (const [key, pos] of Object.entries(newPositions)) {
                if (pos.trade_symbol === sym || key === sym) {
                  newPositions[key] = { ...pos, current_price: p.price, unrealized_pnl: p.pnl };
                  break;
                }
              }
            });
            // P&L is NOT touched here — the 2s poll is the single source of truth.
            // (This handler used to write daily_pnl INTO total_pnl on every price
            // push, making the Total card flip to the daily value constantly.)
            return { ...prev, positions: newPositions };
          });
        }
      }
    };
    // AUTO-RECONNECT — same fix as the live-data socket: keep the price/LTP stream alive
    // across drops and backend restarts so LTP never freezes on the open positions.
    priceWs.onclose = () => { if (!closed) retry = setTimeout(connect, 2000); };
    priceWs.onerror = () => { try { priceWs.close(); } catch (e) {} };
    };
    connect();
    return () => { closed = true; if (retry) clearTimeout(retry); if (priceWs) { try { priceWs.close(); } catch (e) {} } };
  }, []);

  const handleStart = async () => { try { await api.startTrading(); fetchData(); } catch (e) { setError('Failed'); } };
  const handleStop = async () => { try { await api.stopTrading(); fetchData(); } catch (e) { setError('Failed'); } };
  // CLIENT trading on/off toggle: OFF = mode 'off' (no trades); ON = restore the
  // client's last chosen paper/live mode (defaults to paper if never chosen).
  const handleClientToggle = () => {
    const on = clientMode && clientMode !== 'off';
    if (on) return handleMode('off');
    const restore = (clientModeRef.current && clientModeRef.current !== 'off') ? clientModeRef.current : 'paper';
    return handleMode(restore);
  };

  const handleMode = async (m) => {
    // BOTH client and admin now set THEIR OWN trade_mode (admin trades like a client
    // since the master was decommissioned). paper/live/off all go through the same
    // per-user path so the ON/OFF toggle and Paper/Live buttons stay consistent.
    clientModeRef.current = m === 'off' ? clientModeRef.current : m;
    setClientMode(m);
    if (isAdmin) setCurrentMode(m === 'off' ? currentMode : m);
    try {
      const r = await api.setMyTradingPrefs({ trade_mode: m });
      if (r.status !== 'success') setError(r.message || 'Failed to save');
      else setModeChosen(true);
      if (isAdmin && m === 'live') { try { await api.getFunds(); } catch (e) {} }
    } catch (e) { setError('Failed to save your trading mode'); }
    fetchData();
  };
  // SEGMENT ISOLATION (user): the positions view shows ONLY holdings of the
  // currently-selected segment. A trade in another segment (e.g. an NSE option
  // while you're on the MCX tab) must not appear or affect this view.
  const _segGroup = (seg) => {
    const s = String(seg || '').toLowerCase();
    if (s === 'commodity' || s === 'mcx_options') return 'mcx';
    if (s === 'options') return 'options';
    if (s === 'sensex' || s === 'bankex') return 'sensex';
    if (s === 'futures') return 'futures';
    return 'equity';
  };
  // SHOW EVERY position clearly — never hide a real holding (user: "symbol will be
  // seen clearly, don't hide anything on the dashboard"). Each row carries its own
  // segment tag, so all segments' positions are listed together.
  const posEntriesForSegment = () => Object.entries(status?.positions || {});
  const otherSegPositions = () => [];
  const fmtNewsTime = (p) => { try { return new Date(p).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }); } catch { return ''; } };
  // OPTION SELLING toggle (user 2026-09-24): default OFF. Selling premium (the index
  // iron condor) needs real margin, so it only runs when the user switches it on.
  const toggleOptionSelling = async () => {
    const next = !optSell;
    if (next && !window.confirm('Allow OPTION SELLING? The bot may SELL option premium (index iron condor) in addition to buying. Selling needs margin (about Rs.1 lakh+ per index lot) and can lose more than the premium collected if the index moves far. Enable it?')) return;
    setOptSell(next);
    try {
      const r = await api.setMyTradingPrefs({ allow_option_selling: next });
      if (r.status !== 'success') { setOptSell(!next); setError(r.message || 'Failed to save'); }
    } catch (e) { setOptSell(!next); setError('Failed to save option-selling setting'); }
    fetchData();
  };

  const handleSegment = async (s) => {
    if (!isAdmin) {
      // CLIENT: this IS their selected segment — persisted so the engine gives
      // them ONLY this segment's trades (never a segment they didn't pick).
      clientViewTouched.current = true;
      setCurrentSegment(s);
      try { await api.setMyTradingPrefs({ trade_segments: [s] }); } catch (e) {}
      return;
    }
    try {
      const res = await api.setSegment(s);
      if (res.error) { setError(res.error); fetchData(); return; }
      setCurrentSegment(s); fetchData();
    } catch (e) { setError('Failed'); }
  };
  const handleExchange = async (e) => {
    const defaults = { MCX: 'commodity', BSE: 'sensex', NSE: 'equity' };
    if (!isAdmin) {
      // CLIENT: pick exchange -> default segment, persisted as their selection
      clientViewTouched.current = true;
      setActiveExchange(e);
      const seg = defaults[e] || 'equity';
      setCurrentSegment(seg);
      try { await api.setMyTradingPrefs({ trade_segments: [seg] }); } catch (er) {}
      return;
    }
    try {
      const res = await api.setExchange(e);
      if (res.error) { setError(res.error); fetchData(); return; }
      setActiveExchange(e);
      const defaultSeg = defaults[e] || 'equity';
      const segRes = await api.setSegment(defaultSeg);
      if (segRes.error) { setError(segRes.error); fetchData(); return; }
      setCurrentSegment(defaultSeg); fetchData();
    } catch (e) { setError('Failed'); }
  };

  const handleBroker = async (b) => { try { await api.setLiveBroker(b); setCurrentBroker(b); fetchData(); } catch (e) { setError('Failed'); } };

  const handleSendOtp = async () => {
    setBrokerLogin(prev => ({ ...prev, sending: true }));
    try {
      const res = await api.quickLogin();
      if (res.status === 'success') setBrokerLogin({ otpSent: true, otp: '', sending: false });
      else { setError(res.message || 'Could not send OTP — check Settings'); setBrokerLogin(prev => ({ ...prev, sending: false })); }
    } catch (e) { setError('Could not send OTP'); setBrokerLogin(prev => ({ ...prev, sending: false })); }
  };

  const handleVerifyOtp = async () => {
    if (!brokerLogin.otp) return;
    try {
      const res = await api.verifyOTP(brokerLogin.otp);
      if (res.status === 'success') { setBrokerLogin({ otpSent: false, otp: '', sending: false }); fetchData(); }
      else setError(res.message || 'Invalid OTP');
    } catch (e) { setError('OTP verification failed'); }
  };

  if (loading) return <div className="loading">Loading...</div>;

  const needsBrokerLogin = status?.is_api_configured && !status?.is_logged_in;
  const myBrokers = status?.my_brokers || null;

  return (
    <div className="dashboard">
      {/* BLOCKER WATCHDOG banner: system problems (not market conditions) that stop trading */}
      {(status?.system_alerts || []).length > 0 && (
        <div style={{ background: 'rgba(255,60,60,0.12)', border: '1px solid #ff4d4d', borderRadius: 8, padding: '0.6rem 0.8rem', margin: '0 0 0.8rem' }}>
          <div style={{ color: '#ff6b6b', fontWeight: 700, marginBottom: '0.3rem' }}>⚠ Action needed — trading is being blocked</div>
          {status.system_alerts.map((a, i) => (
            <div key={i} style={{ color: '#ffd0d0', fontSize: '0.82rem', margin: '0.15rem 0' }}>
              • {a.text}{a.since ? <span style={{ color: '#aa8888' }}> (since {a.since})</span> : null}
            </div>
          ))}
        </div>
      )}
      {/* CLIENT: broker banner ONLY when they've chosen LIVE — paper needs no broker */}
      {!isAdmin && clientMode === 'live' && myBrokers && !myBrokers.any_connected && (
        <div className="card full-width" style={{ border: '1px solid #ffb020', marginBottom: '1rem' }}>
          <div className="card-header">
            <span className="card-title" style={{ color: '#ffb020' }}>⚡ Live mode needs your broker connected</span>
          </div>
          <p style={{ color: '#888', fontSize: '0.8rem' }}>
            {myBrokers.any_configured
              ? 'Credentials saved but today\'s session is not active — go to Settings → My Broker Account and press Connect (Fyers) or login with OTP (mStock). Or switch to Paper to trade without a broker.'
              : 'Connect your own broker in Settings → My Broker Account for live trading. Or use Paper mode — it needs no broker.'}
          </p>
        </div>
      )}
      {isAdmin && dec && (
        <div className="card full-width" style={{ border: '1px solid #ffd700', marginBottom: '0.8rem', padding: '0.6rem 0.8rem' }}>
          <div style={{ color: '#ffd700', fontWeight: 700, marginBottom: '0.3rem' }}>What your decisions teach
            <span style={{ color: '#888', fontWeight: 400, fontSize: '0.75rem' }}> · {dec.total} decisions recorded, {dec.with_result} with a result
              {' '}(approve / manual = real trade; reject / no answer = what it would have made)</span></div>
          {dec.with_result === 0 ? <div style={{ color: '#888', fontSize: '0.8rem' }}>No results yet — every approve, reject and manual order is recorded with what the AI saw; patterns appear here after a few days.</div> : (
            <div style={{ display: 'flex', gap: '1.2rem', flexWrap: 'wrap', fontSize: '0.78rem' }}>
              {[['by_decision', 'By decision'], ['by_decision_lane', 'Decision x lane'], ['by_decision_side', 'Decision x CE/PE'], ['by_decision_hour', 'Decision x hour'], ['by_decision_score', 'Decision x AI score']].map(([k, lab]) => (
                <div key={k}>
                  <div style={{ color: '#888', marginBottom: 2 }}>{lab}</div>
                  {Object.entries(dec[k] || {}).sort().map(([g, v]) => (
                    <div key={g}><b>{g}</b>: {v.n} · win {v.win_pct}% · avg <span style={{ color: v.avg_pct >= 0 ? '#00ff88' : '#ff6b6b' }}>{v.avg_pct >= 0 ? '+' : ''}{v.avg_pct}%</span></div>))}
                </div>))}
            </div>)}
          {(dec.recent || []).filter(r => ['approve', 'reject', 'expired', 'manual'].includes(r.decision)).length > 0 && (
            <div style={{ marginTop: '0.5rem', fontSize: '0.78rem' }}>
              <div style={{ color: '#888', marginBottom: 2 }}>Your last choices — what the trade did afterwards</div>
              {(dec.recent || []).filter(r => ['approve', 'reject', 'expired', 'manual'].includes(r.decision)).slice(-12).reverse().map(r => {
                const has = r.pct !== undefined && r.pct !== null;
                const up = has && r.pct > 0;
                const took = r.decision === 'approve' || r.decision === 'manual';
                const verdict = !has ? 'result pending' : took ? (up ? '✔ good call' : '✘ lost') : (up ? '✘ missed a winner' : '✔ good skip');
                return (
                  <div key={r.id} style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', borderBottom: '1px solid #222', padding: '2px 0' }}>
                    <span style={{ color: '#888', minWidth: 42 }}>{String(r.at || '').slice(11, 16)}</span>
                    <b style={{ minWidth: 70, color: took ? '#00d9ff' : '#aaa' }}>{r.decision === 'expired' ? 'no answer' : r.decision}</b>
                    <span style={{ minWidth: 150 }}>{r.trade_symbol}</span>
                    <span style={{ color: !has ? '#888' : up ? '#00ff88' : '#ff6b6b', minWidth: 70 }}>{has ? `${r.pct > 0 ? '▲ +' : '▼ '}${r.pct}%` : '…'}</span>
                    <span style={{ color: verdict.startsWith('✔') ? '#00ff88' : verdict.startsWith('✘') ? '#ff6b6b' : '#888' }}>{verdict}</span>
                  </div>);
              })}
            </div>)}
          <div style={{ color: '#666', fontSize: '0.7rem', marginTop: '0.3rem' }}>A pattern that makes money becomes a rule only after a backtest and your OK.</div>
        </div>
      )}
      {isAdmin && miss && (
        <div className="card full-width" style={{ border: '1px solid #c084fc', marginBottom: '0.8rem', padding: '0.6rem 0.8rem' }}>
          <div style={{ color: '#c084fc', fontWeight: 700, marginBottom: '0.3rem', display: 'flex', gap: '0.6rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <span>Missed trades — which rule blocked winners</span>
            <span style={{ color: '#888', fontWeight: 400, fontSize: '0.75rem' }}>
              {miss.total} skipped signals checked: <b style={{ color: '#00ff88' }}>{miss.win} would win</b> · <b style={{ color: '#ff4444' }}>{miss.lose} would lose</b> · watching {miss.watching} now
            </span>
            <select value={missDays} onChange={e => setMissDays(+e.target.value)} style={{ fontSize: '0.75rem' }}>
              <option value={1}>today</option><option value={5}>5 days</option><option value={20}>20 days</option>
            </select>
          </div>
          {miss.gates.length === 0 ? <div style={{ color: '#888', fontSize: '0.8rem' }}>No finished checks yet — each skipped signal is watched for up to 60 min (would-win = the stock moves 0.8% our way before 0.5% against; indices 0.32% / 0.2%).</div> : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ fontSize: '0.78rem', width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr style={{ color: '#888', textAlign: 'left' }}><th>Rule that skipped</th><th>Checked</th><th>Would win</th><th>Would lose</th><th>Flat</th><th>AI verdict</th></tr></thead>
                <tbody>
                  {miss.gates.map(g => (
                    <tr key={g.gate} style={{ borderTop: '1px solid #333' }}>
                      <td><b>{g.gate}</b></td><td>{g.n}</td>
                      <td style={{ color: '#00ff88' }}>{g.win}</td><td style={{ color: '#ff4444' }}>{g.lose}</td><td>{g.flat}</td>
                      <td style={{ color: g.flag ? '#ffb020' : (g.net_blocked_winners > 0 ? '#ddd' : '#888') }}>{g.verdict}</td>
                    </tr>))}
                </tbody>
              </table>
            </div>)}
          <div style={{ color: '#666', fontSize: '0.7rem', marginTop: '0.3rem' }}>Record only — rules are changed only after a backtest and your OK.</div>
        </div>
      )}
      {/* ADMIN: master broker reconnect — one-tap OTP, no Settings needed */}
      {isAdmin && needsBrokerLogin && (
        <div className="card full-width" style={{ border: '1px solid #ffb020', marginBottom: '1rem' }}>
          <div className="card-header">
            <span className="card-title" style={{ color: '#ffb020' }}>⚡ Broker not connected — today's session expired</span>
          </div>
          {!brokerLogin.otpSent ? (
            <button className="btn btn-start" disabled={brokerLogin.sending} onClick={handleSendOtp}>
              {brokerLogin.sending ? 'Sending OTP…' : 'Connect mStock — Send OTP'}
            </button>
          ) : (
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                type="text" placeholder="Enter OTP" value={brokerLogin.otp} autoFocus
                onChange={e => setBrokerLogin(prev => ({ ...prev, otp: e.target.value }))}
                onKeyDown={e => e.key === 'Enter' && handleVerifyOtp()}
                style={{ width: '140px' }}
              />
              <button className="btn btn-start" onClick={handleVerifyOtp}>Verify & Connect</button>
              <button className="btn" style={{ background: '#222', color: '#888' }} onClick={handleSendOtp}>Resend</button>
            </div>
          )}
          <p style={{ color: '#888', fontSize: '0.75rem', marginTop: '0.5rem' }}>
            Uses your saved login — no API key or password typing. (Save credentials once in Settings → "Remember password".)
          </p>
        </div>
      )}

      {/* Mode, Segment & Broker */}
      <div className="card full-width">
        <div className="card-header"><span className="card-title">Mode & Segment</span></div>
        <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '200px' }}>
            <label style={{ color: '#888', fontSize: '0.8rem' }}>Mode</label>
            {(() => {
              // client sees BOTH modes as a view toggle over their own books
              const shownMode = (clientMode === 'paper' || clientMode === 'live') ? clientMode : currentMode;
              return (
                <>
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.3rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <button className={`btn ${shownMode === 'paper' ? 'btn-paper-active' : 'btn-paper'}`} onClick={() => handleMode('paper')}>Paper</button>
                    <button className={`btn ${shownMode === 'live' ? 'btn-live-active' : 'btn-live'}`} onClick={() => handleMode('live')}>Live</button>
                    {isAdmin && (() => {
                      // ADMIN trading ON/OFF toggle — reflects the ADMIN's OWN trade_mode
                      // (it trades like a client now), NOT the dead master switch. OFF sets
                      // the admin's mode to 'off'; ON restores its last paper/live choice.
                      const on = clientMode && clientMode !== 'off';
                      return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: '0.4rem' }}>
                          <button
                            onClick={handleClientToggle}
                            aria-pressed={on}
                            title={on ? 'Trading ON — click to turn OFF' : 'Trading OFF — click to turn ON'}
                            style={{
                              position: 'relative', width: 88, minWidth: 88, height: 34, borderRadius: 17,
                              border: 'none', cursor: 'pointer', padding: 0, flex: '0 0 auto', overflow: 'hidden',
                              background: on ? '#16a34a' : '#6b7280', transition: 'background 0.25s ease',
                              boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.4)',
                            }}
                          >
                            <span style={{
                              position: 'absolute', top: 0, bottom: 0, display: 'flex', alignItems: 'center',
                              left: on ? 12 : 'auto', right: on ? 'auto' : 12,
                              color: '#fff', fontSize: '0.62rem', fontWeight: 700, letterSpacing: 0.5,
                            }}>{on ? 'ON' : 'OFF'}</span>
                            <span style={{
                              position: 'absolute', top: 3, left: on ? 58 : 3, width: 28, height: 28,
                              borderRadius: '50%', background: '#fff', transition: 'left 0.25s ease',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
                            }} />
                          </button>
                          <span style={{ fontSize: '0.75rem', fontWeight: 700, color: on ? '#16a34a' : '#9ca3af' }}>
                            {on ? 'TRADING ON' : 'TRADING OFF'}
                          </span>
                        </div>
                      );
                    })()}
                    {!isAdmin && (() => {
                      // ON/OFF toggle replaces the old Off button — OFF stops trades for
                      // this client, ON restores their chosen Paper/Live mode.
                      const on = clientMode && clientMode !== 'off';
                      return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: '0.4rem' }}>
                          <button
                            onClick={handleClientToggle}
                            aria-pressed={on}
                            title={on ? 'Trading ON — click to turn OFF' : 'Trading OFF — click to turn ON'}
                            style={{
                              position: 'relative', width: 88, minWidth: 88, height: 34, borderRadius: 17,
                              border: 'none', cursor: 'pointer', padding: 0, flex: '0 0 auto', overflow: 'hidden',
                              background: on ? '#16a34a' : '#6b7280', transition: 'background 0.25s ease',
                              boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.4)',
                            }}
                          >
                            <span style={{
                              position: 'absolute', top: 0, bottom: 0, display: 'flex', alignItems: 'center',
                              left: on ? 12 : 'auto', right: on ? 'auto' : 12,
                              color: '#fff', fontSize: '0.62rem', fontWeight: 700, letterSpacing: 0.5,
                            }}>{on ? 'ON' : 'OFF'}</span>
                            <span style={{
                              position: 'absolute', top: 3, left: on ? 58 : 3, width: 28, height: 28,
                              borderRadius: '50%', background: '#fff', transition: 'left 0.25s ease',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
                            }} />
                          </button>
                          <span style={{ fontSize: '0.75rem', fontWeight: 700, color: on ? '#16a34a' : '#9ca3af' }}>
                            {on ? 'TRADING ON' : 'TRADING OFF'}
                          </span>
                        </div>
                      );
                    })()}
                  </div>
                  {isAdmin && (
                    <p style={{ color: (clientMode === 'off' || !clientMode) ? '#ffb020' : '#666', fontSize: '0.68rem', marginTop: '0.2rem' }}>
                      {(clientMode === 'off')
                        ? 'YOUR (admin) trading is OFF — engine still running, clients still trading (click Paper/Live to turn ON)'
                        : (!clientMode)
                          ? 'Choose Paper or Live to trade YOUR admin account (engine + clients unaffected)'
                          : `YOUR (admin) trading is ON — the engine trades your ${clientMode === 'live' ? 'broker account' : 'paper book'} automatically`}
                    </p>
                  )}
                  {!isAdmin && (
                    <p style={{ color: !modeChosen ? '#ffb020' : '#666', fontSize: '0.68rem', marginTop: '0.2rem' }}>
                      {!modeChosen
                        ? '⚠ Choose Paper or Live to START receiving trades — until you choose, nothing trades in your name'
                        : clientMode === 'off'
                          ? 'Your trading is OFF — signals are skipped for your account (logged below)'
                          : `YOUR trading mode — the engine trades your ${clientMode === 'live' ? 'broker account' : 'paper book'} automatically`}
                    </p>
                  )}
                  {shownMode === 'paper' && <p style={{ color: '#00d9ff', marginTop: '0.3rem', fontSize: '0.75rem' }}>Paper Bal: ₹{paperBalance?.toFixed(2)}</p>}
                  {shownMode === 'live' && (
                    <p style={{ color: '#ff6b6b', marginTop: '0.3rem', fontSize: '0.75rem' }}>
                      {isAdmin ? 'Live Bal' : 'My Broker Bal'}:{' '}
                      {isAdmin
                        ? ((status?.live_balance_fetched || (liveBalance || 0) > 0)
                            ? `₹${Number(liveBalance || 0).toFixed(2)}`
                            : status?.broker_connected ? 'Fetching...' : 'Broker login required')
                        : (myBrokers?.any_connected
                            ? ((liveBalance || 0) > 0 ? `₹${Number(liveBalance).toFixed(2)}` : 'Fetching from your broker...')
                            : 'Connect your broker (Settings)')}
                    </p>
                  )}
                </>
              );
            })()}
          </div>
          <div style={{ flex: 1, minWidth: '200px' }}>
            <label style={{ color: '#888', fontSize: '0.8rem' }}>Exchange</label>
            <select
              value={activeExchange}
              onChange={(e) => handleExchange(e.target.value)}
              style={{ display: 'block', marginTop: '0.3rem', padding: '0.5rem', background: '#1a1a2e', color: '#fff', border: '1px solid #444', borderRadius: '4px', fontSize: '0.85rem', width: '100%' }}
            >
              <option value="NSE">NSE</option>
              <option value="MCX">MCX</option>
              <option value="BSE">BSE</option>
            </select>
            {!isAdmin && <p style={{ color: '#666', fontSize: '0.68rem', marginTop: '0.2rem' }}>Browse any exchange — trading itself follows the admin's engine</p>}
          </div>
          <div style={{ flex: 1, minWidth: '200px' }}>
            <label style={{ color: '#888', fontSize: '0.8rem' }}>Type</label>
            <select
              value={currentSegment}
              onChange={(e) => handleSegment(e.target.value)}
              style={{ display: 'block', marginTop: '0.3rem', padding: '0.5rem', background: '#1a1a2e', color: '#fff', border: '1px solid #444', borderRadius: '4px', fontSize: '0.85rem', width: '100%' }}
            >
              {activeExchange === 'NSE' && (
                <>
                  <option value="equity" disabled={segDisabled('equity')}>{segLabel('equity', 'Equity')}</option>
                  <option value="options" disabled={segDisabled('options')}>{segLabel('options', 'Options')}</option>
                  <option value="futures" disabled={segDisabled('futures')}>{segLabel('futures', 'Futures')}</option>
                </>
              )}
              {activeExchange === 'MCX' && (
                <>
                  <option value="commodity" disabled={segDisabled('commodity')}>{segLabel('commodity', 'Commodity Futures')}</option>
                  <option value="mcx_options" disabled={segDisabled('mcx_options')}>{segLabel('mcx_options', 'Commodity Options')}</option>
                </>
              )}
              {activeExchange === 'BSE' && (
                <option value="sensex" disabled={segDisabled('sensex')}>{segLabel('sensex', 'Sensex')}</option>
              )}
            </select>
            <p style={{ color: marketOpen ? '#00d9ff' : '#888', marginTop: '0.3rem', fontSize: '0.75rem' }}>
              {stockCount} instruments
              {marketOpen
                ? <span style={{ color: '#16a34a', marginLeft: '0.4rem' }}>● scanning live</span>
                : <span style={{ color: '#ff6b6b', marginLeft: '0.4rem', fontWeight: 700 }}>⏸ MARKET CLOSED</span>}
            </p>
            {!marketOpen && marketStatus && (
              <p style={{ color: '#ff6b6b', marginTop: '0.15rem', fontSize: '0.68rem' }}>{marketStatus}</p>
            )}
            {segInfo(currentSegment)?.reason && (
              <p style={{ color: segInfo(currentSegment).available ? '#f59e0b' : '#ff4444', marginTop: '0.2rem', fontSize: '0.72rem' }}>
                {segInfo(currentSegment).reason}
              </p>
            )}
            {activeExchange === 'NSE' && currentSegment === 'equity' && (
              <div style={{ marginTop: '0.5rem' }}>
                <label style={{ color: '#888', fontSize: '0.8rem' }}>Product</label>
                <select
                  value={equityProduct}
                  onChange={async (e) => {
                    const v = e.target.value;
                    productTouchedAt.current = Date.now();
                    setEquityProduct(v);
                    try {
                      // each user picks their OWN product (saved in their trading prefs);
                      // admin also updates the global default
                      const res = await api.setMyTradingPrefs({ equity_product: v });
                      if (res && res.status === 'error') { setError(res.message || 'Failed to set product'); return; }
                      if (isAdmin) { await api.setEquityProduct(v); }
                      setEquityProduct(v);
                    } catch (err) { setError('Failed to set product'); }
                  }}
                  style={{ display: 'block', marginTop: '0.3rem', padding: '0.4rem', background: '#1a1a2e', color: '#fff', border: '1px solid #444', borderRadius: '4px', fontSize: '0.8rem', width: '100%' }}
                >
                  <option value="CNC">Cash (CNC) — 1x, delivery</option>
                  <option value="MIS">Intraday (MIS) — ~5x margin</option>
                  <option value="MTF">MTF — ~4x funded</option>
                </select>
              </div>
            )}
          </div>
          <div style={{ flex: 1, minWidth: '200px' }}>
            <label style={{ color: '#888', fontSize: '0.8rem' }}>{isAdmin ? 'Broker' : 'My Brokers'}</label>
            {isAdmin ? (
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.3rem' }}>
                <button className={`btn ${currentBroker === 'mstock' ? 'btn-segment-active' : 'btn-segment'}`} onClick={() => handleBroker('mstock')}>mStock</button>
                <button className={`btn ${currentBroker === 'fyers' ? 'btn-segment-active' : 'btn-segment'}`} onClick={() => handleBroker('fyers')}>Fyers</button>
              </div>
            ) : (
              <div style={{ marginTop: '0.3rem', fontSize: '0.8rem' }}>
                {/* only brokers the USER actually set up — no phantom mStock row */}
                {myBrokers?.mstock_configured && (
                  <span style={{ color: myBrokers.mstock_connected ? '#00ff88' : '#ffb020', display: 'block' }}>
                    ● mStock {myBrokers.mstock_connected ? 'connected' : 'saved — login needed'}
                  </span>
                )}
                {myBrokers?.fyers_configured && (
                  <span style={{ color: myBrokers.fyers_connected ? '#00ff88' : '#ffb020', display: 'block' }}>
                    ● Fyers {myBrokers.fyers_connected ? 'connected' : 'saved — connect needed'}
                  </span>
                )}
                {!myBrokers?.any_configured && (
                  <span style={{ color: '#888' }}>No broker set up — Settings → My Broker Account</span>
                )}
              </div>
            )}
            {segAvail && !segAvail.broker_connected && (
              <p style={{ color: '#ff4444', marginTop: '0.2rem', fontSize: '0.7rem' }}>
                {isAdmin
                  ? `${segAvail.broker} is not logged in — data & orders need your broker connected`
                  : 'No broker connected — Settings → My Broker Account'}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Status card removed — trading ON/OFF now lives inline in Mode & Segment
          for both admin and clients (no separate, odd-looking Status card). */}

      {/* ── STOCK OPTIONS IN THE BACKGROUND (ADMIN ONLY, owner 2026-11-03): which stocks the virtual book traded today ── */}
      {isAdmin && stockBg && (() => {
        const fm = (v) => (v == null ? '—' : `${v >= 0 ? '+' : '−'}₹${Math.abs(Math.round(v)).toLocaleString('en-IN')}`);
        const cc = (v) => (v == null ? '#888' : v > 0 ? '#00ff88' : v < 0 ? '#ff6b6b' : '#aaa');
        const inView = (r) => (stockBgView === 'pass' ? (r.kind === 'bot' && r.pass) : r.kind === stockBgView);
        const op = (stockBg.open || []).filter(inView);
        const cl = (stockBg.closed || []).filter(inView);
        const tt = stockBgView === 'pass' ? stockBg.tot_pass : stockBgView === 'bot' ? stockBg.tot_bot : stockBg.tot_skipped;
        const nOpen = (f) => (stockBg.open || []).filter(f).length;
        const why = (r) => (r.kind === 'skipped' ? `bot skipped: ${r.gate || ''}`
          : r.pass ? 'passed every AI + ML check' : `stopped by: ${(r.checks || []).join('; ')}`);
        const opRs = op.reduce((a, r) => a + (r.rs || 0), 0);
        return (
          <div className="card" style={{ gridColumn: '1 / -1' }}>
            <div className="card-header" style={{ flexWrap: 'wrap', gap: 6 }}>
              <span className="card-title">📊 Stock options — background trades {stockBg.real_on ? '' : '(virtual, no money)'}</span>
              <span style={{ display: 'flex', gap: 4 }}>
                <button className={`btn ${stockBgView === 'pass' ? 'btn-start' : ''}`} style={{ padding: '2px 8px', fontSize: '0.72rem' }} onClick={() => setStockBgView('pass')}>✔ AI + ML approved ({(stockBg.tot_pass?.n || 0) + nOpen(r => r.kind === 'bot' && r.pass)})</button>
                <button className={`btn ${stockBgView === 'bot' ? 'btn-start' : ''}`} style={{ padding: '2px 8px', fontSize: '0.72rem' }} onClick={() => setStockBgView('bot')}>All bot picks ({(stockBg.tot_bot?.n || 0) + nOpen(r => r.kind === 'bot')})</button>
                <button className={`btn ${stockBgView === 'skipped' ? 'btn-start' : ''}`} style={{ padding: '2px 8px', fontSize: '0.72rem' }} onClick={() => setStockBgView('skipped')}>Skipped signals ({(stockBg.tot_skipped?.n || 0) + (stockBg.open || []).filter(r => r.kind === 'skipped').length})</button>
              </span>
            </div>
            <div style={{ fontSize: '0.78rem', color: '#9aa', marginBottom: 6 }}>
              Today closed: <b>{tt?.n || 0}</b> · won {tt?.win || 0} · <b style={{ color: cc(tt?.rs) }}>{fm(tt?.rs)}</b>
              {' '}· open now: <b>{op.length}</b>{op.length ? <> · <b style={{ color: cc(opRs) }}>{fm(opRs)}</b> live</> : null}
              <span style={{ color: '#666' }}> · 1 lot each, real option price + spread + ₹110 charges · My Decisions shows when it is READY for real trading</span>
              {stockBgView === 'pass' && <div style={{ color: '#666', marginTop: 2 }}>AI + ML approved = AI score ≥ {stockBg.score_bar}, stock-model ML win-chance ≥ {Math.round((stockBg.ml_bar || 0) * 100)}%, 10:00-14:00, premium ₹20-150, not already jumped (NO CHASE), Renko brick agrees. Only this group decides READY.</div>}
            </div>
            <div style={{ overflowX: 'auto', maxHeight: 360, overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.76rem' }}>
                <thead><tr style={{ color: '#888', textAlign: 'left', position: 'sticky', top: 0, background: 'var(--card-bg, #12161f)' }}>
                  <th>Time</th><th>Stock</th><th>Contract</th><th>AI</th><th>ML</th><th>Entry</th><th>Exit / Now</th><th>P&amp;L</th><th>Status · why picked</th>
                </tr></thead>
                <tbody>
                  {op.map((r, i) => (
                    <tr key={'o' + i} style={{ borderTop: '1px solid #222' }}>
                      <td style={{ padding: '3px 4px' }}>{String(r.time || '').slice(0, 5)}</td><td><b>{r.sym}</b></td><td>{r.ts_}</td>
                      <td>{r.score}</td><td>{r.ml != null ? `${Math.round(r.ml * 100)}%` : '—'}</td>
                      <td>₹{r.e}</td><td>{r.px ? `₹${r.px}` : '…'}</td>
                      <td style={{ color: cc(r.rs), fontWeight: 700 }}>{fm(r.rs)} {r.pct != null && <span style={{ fontWeight: 400 }}>({r.pct > 0 ? '+' : ''}{r.pct}%)</span>}</td>
                      <td style={{ color: '#00d9ff' }}>OPEN · stop ₹{r.stop} <span style={{ color: '#777' }}>· {why(r)}</span></td>
                    </tr>))}
                  {cl.map((r, i) => (
                    <tr key={'c' + i} style={{ borderTop: '1px solid #222' }}>
                      <td style={{ padding: '3px 4px' }}>{String(r.time || '').slice(0, 5)}</td><td><b>{r.sym}</b></td><td>{r.ts_}</td>
                      <td>{r.score}</td><td>{r.ml != null ? `${Math.round(r.ml * 100)}%` : '—'}</td>
                      <td>₹{r.e}</td><td>₹{r.x}</td>
                      <td style={{ color: cc(r.rs), fontWeight: 700 }}>{fm(r.rs)} <span style={{ fontWeight: 400 }}>({r.pct > 0 ? '+' : ''}{r.pct}%)</span></td>
                      <td style={{ color: '#888' }}>{r.exit} · {r.mins} min · best {r.best_pct > 0 ? '+' : ''}{r.best_pct}% <span style={{ color: '#666' }}>· {why(r)}</span></td>
                    </tr>))}
                  {!op.length && !cl.length && <tr><td colSpan={9} style={{ color: '#888', padding: 6 }}>{stockBgView === 'pass' ? 'No stock pick passed every AI + ML check yet today (the others are in "All bot picks").' : 'No background stock trades yet today.'}</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {/* ── LEARNING BRAIN (ADMIN ONLY): what the app is learning every night ── */}
      {isAdmin && brain && (
        <div className="card" style={{ gridColumn: '1 / -1' }}>
          <div className="card-header">
            <span className="card-title">🧠 Learning Brain</span>
            <span style={{ color: '#666', fontSize: '0.72rem' }}>self-learning status · updates nightly after close</span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.4rem', fontSize: '0.8rem', marginBottom: '0.6rem' }}>
            <span>Live model AUC: <b style={{ color: (brain.model?.auc || 0) >= 0.55 ? '#00ff88' : '#ffb020' }}>{brain.model?.auc ?? '—'}</b>
              <span style={{ color: '#666' }}> {brain.model?.trained_on ? `(${brain.model.trained_on} trades, ${brain.model.updated})` : ''}</span></span>
            <span>Avoiding: <b style={{ color: brain.quarantine_count ? '#ffb020' : '#00ff88' }}>{brain.quarantine_count}</b> chronic-loser stocks</span>
          </div>
          {brain.models && (
            // SEPARATE MODELS (owner 2026-11-03): index options and stock options each with their own AUC
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 8, marginBottom: '0.6rem' }}>
              {[['📈 Index options AUC', 'index'], ['🏢 Stock options AUC', 'stock']].map(([t, k]) => {
                const m = brain.models[k] || {};
                const a = m.test_auc;
                const ac = a == null ? '#888' : a >= 0.6 ? '#00ff88' : a > 0.55 ? '#ffb020' : '#ff6b6b';
                const la = m.live_auc;
                const lc = la == null ? '#888' : la >= 0.55 ? '#00ff88' : la >= 0.5 ? '#ffb020' : '#ff6b6b';
                return (
                  <div key={k} style={{ padding: '0.5rem 0.7rem', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, fontSize: '0.76rem', color: '#9aa' }}>
                    <div style={{ fontWeight: 700, color: '#ddd', marginBottom: 2 }}>{t}</div>
                    <div>Model (held-out test): <b style={{ color: ac }}>{m.loaded ? a : 'first training tonight'}</b>
                      {m.loaded && <span style={{ color: '#666' }}> · {m.trained_on} examples</span>}</div>
                    <div>On real results: <b style={{ color: lc }}>{la ?? '—'}</b> <span style={{ color: '#666' }}>({m.live_n || 0} trades{(m.live_n || 0) < 20 ? ', needs 20' : ''})</span></div>
                    {m.answers_with && m.answers_with !== k && <div style={{ color: '#666' }}>now using the combined model</div>}
                  </div>
                );
              })}
            </div>
          )}
          {brain.last_retrain && (
            <div style={{ color: '#9aa', fontSize: '0.74rem', marginBottom: '0.3rem' }}>
              Last retrain ({brain.last_retrain.at}): {brain.last_retrain.result}
            </div>
          )}
          {brain.last_review && (
            <div style={{ color: '#9aa', fontSize: '0.74rem', marginBottom: '0.3rem' }}>
              Last self-review ({brain.last_review.date} {brain.last_review.at}): {brain.last_review.trades} trades · {brain.last_review.win_rate}% win · net ₹{brain.last_review.net}
              {Array.isArray(brain.last_review.new_quarantine) && brain.last_review.new_quarantine.length > 0 && <span> · newly avoided: {brain.last_review.new_quarantine.join(', ')}</span>}
            </div>
          )}
          {brain.quarantine?.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginTop: '0.4rem' }}>
              {brain.quarantine.map((q, i) => (
                <span key={i} style={{ fontSize: '0.72rem', background: '#2a1a1a', border: '1px solid #5a2a2a', color: '#ff9a9a', borderRadius: 4, padding: '0.15rem 0.5rem' }}>
                  ⛔ {q.symbol} <span style={{ color: '#996' }}>{q.days_left}d</span>
                </span>
              ))}
            </div>
          )}
          <div style={{ color: '#666', fontSize: '0.68rem', marginTop: '0.5rem' }}>
            Note: the model only upgrades if a fresh nightly model beats the live one; chronic-loser stocks are auto-avoided for ~5 days.
          </div>
        </div>
      )}

      {/* ── POST-MARKET DECISION REVIEW (ADMIN ONLY — aggregate across all users) ── */}
      {isAdmin && review && !review.error && (
        <div className="card" style={{ gridColumn: '1 / -1' }}>
          <div className="card-header">
            <span className="card-title">🧠 Decision Review — {review.date}</span>
            <span style={{ fontSize: '0.62rem', color: '#888' }}>what the AI learned from today's trades (5-min entry/exit tracking)</span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.8rem', margin: '0.5rem 0', fontSize: '0.8rem' }}>
            <span>Trades: <b>{review.trades}</b></span>
            <span>Win rate: <b style={{ color: review.win_rate >= 40 ? '#00ff88' : '#ffb020' }}>{review.win_rate}%</b></span>
            <span>Avg win: <b style={{ color: '#00ff88' }}>₹{review.avg_win}</b></span>
            <span>Avg loss: <b style={{ color: '#ff5b5b' }}>₹{Math.abs(review.avg_loss)}</b></span>
            <span>Net: <b style={{ color: review.net_pnl >= 0 ? '#00ff88' : '#ff5b5b' }}>₹{review.net_pnl}</b></span>
            <span>Good entries (5m): <b>{review.entry_good_rate}%</b> <span style={{ color: '#666' }}>({review.entries_tracked})</span></span>
            <span>Premature exits: <b style={{ color: review.premature_exit_rate >= 40 ? '#ffb020' : '#9aa' }}>{review.premature_exit_rate}%</b> <span style={{ color: '#666' }}>({review.exits_tracked})</span></span>
          </div>
          {review.categories && (
            <div style={{ fontSize: '0.73rem', color: '#aab', margin: '0.3rem 0' }}>
              Entries → <span style={{ color: '#00ff88' }}>{review.categories.entries.good} good</span> · <span style={{ color: '#ff5b5b' }}>{review.categories.entries.bad} bad</span> · {review.categories.entries.neutral} neutral &nbsp;|&nbsp;
              Exits → <span style={{ color: '#00ff88' }}>{review.categories.exits.good} good</span> · <span style={{ color: '#ffb020' }}>{review.categories.exits.premature_profit} premature</span> · <span style={{ color: '#ff5b5b' }}>{review.categories.exits.loss_cut_recovered} loss-cut-recovered</span>
            </div>
          )}
          {Array.isArray(review.recommendations) && review.recommendations.map((r, i) => (
            <p key={i} style={{ color: '#ffcf6b', fontSize: '0.76rem', margin: '0.2rem 0' }}>▸ {r}</p>
          ))}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.2rem', marginTop: '0.5rem' }}>
            {Array.isArray(review.good_entries_list) && review.good_entries_list.length > 0 && (
              <div style={{ flex: '1 1 260px' }}>
                <div style={{ color: '#00ff88', fontSize: '0.72rem', fontWeight: 600, marginBottom: '0.2rem' }}>✅ Good entries (turned a profit)</div>
                {review.good_entries_list.map((e, i) => (
                  <div key={i} style={{ fontSize: '0.73rem', color: '#ccc' }}>
                    {e.symbol} <span style={{ color: '#00ff88' }}>+{e.pnl_pct}%</span>
                  </div>
                ))}
              </div>
            )}
            {Array.isArray(review.bad_entries) && review.bad_entries.length > 0 && (
              <div style={{ flex: '1 1 260px' }}>
                <div style={{ color: '#ff8080', fontSize: '0.72rem', fontWeight: 600, marginBottom: '0.2rem' }}>❌ Bad entries (went wrong within 5 min)</div>
                {review.bad_entries.map((e, i) => (
                  <div key={i} style={{ fontSize: '0.73rem', color: '#ccc' }}>
                    {e.symbol} <span style={{ color: '#ff5b5b' }}>{e.move_5m_pct}%</span> {e.label ? <span style={{ color: '#777' }}>· {e.label.toLowerCase()}</span> : null}
                  </div>
                ))}
              </div>
            )}
            {Array.isArray(review.bad_exits) && review.bad_exits.length > 0 && (
              <div style={{ flex: '1 1 260px' }}>
                <div style={{ color: '#ffb020', fontSize: '0.72rem', fontWeight: 600, marginBottom: '0.2rem' }}>⚠ Premature exits (ran up after we sold)</div>
                {review.bad_exits.map((e, i) => (
                  <div key={i} style={{ fontSize: '0.73rem', color: '#ccc' }}>
                    {e.symbol} <span style={{ color: '#00d9ff' }}>+{e.left_pct}%</span> best after exit{e.move_5m ? <span style={{ color: '#00d9ff' }}> ({e.move_5m > 0 ? '+' : ''}{e.move_5m}% in 5m)</span> : null} <span style={{ color: '#777' }}>· {e.category}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── LIVE MARKET NEWS ── */}
      {news && news.length > 0 && (
        <div className="card" style={{ gridColumn: '1 / -1' }}>
          <div className="card-header">
            <span className="card-title">📰 Live Market News</span>
            <span style={{ fontSize: '0.62rem', color: '#888' }}>
              {news.length} headlines · updates every 5s · free feeds are delayed vs the market
            </span>
          </div>
          <div style={{ maxHeight: '240px', overflowY: 'auto', marginTop: '0.4rem' }}>
            {news.map((n, i) => {
              const c = n.sentiment === 'positive' ? '#00ff88' : n.sentiment === 'negative' ? '#ff5b5b' : '#99a';
              return (
                <div key={i} style={{ display: 'flex', gap: '0.5rem', padding: '0.35rem 0', borderBottom: '1px solid #ffffff10', fontSize: '0.78rem', alignItems: 'baseline' }}>
                  <span style={{ color: c, fontSize: '0.7rem' }} title={n.sentiment}>●</span>
                  <span title={n.region} style={{ fontSize: '0.62rem' }}>{n.region === 'Global' ? '🌍' : '🇮🇳'}</span>
                  <a href={n.link} target="_blank" rel="noreferrer" style={{ color: '#dcdce6', textDecoration: 'none', flex: 1 }}>{n.title}</a>
                  <span style={{ color: '#777', fontSize: '0.65rem', whiteSpace: 'nowrap' }}>{n.source}</span>
                  <span style={{ color: '#666', fontSize: '0.62rem', whiteSpace: 'nowrap' }}>{fmtNewsTime(n.pub)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── GLOBAL MARKETS (24/7 world watcher) ── */}
      {globalCtx?.regime && (
        <div className="card" style={{ gridColumn: '1 / -1' }}>
          <div className="card-header" style={{ cursor: 'pointer' }} onClick={() => setShowGlobal(s => !s)}>
            <span className="card-title">🌍 Global Markets</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span className="signal-badge" style={{
                background: globalCtx.regime.label === 'Risk-ON' ? '#16a34a'
                          : globalCtx.regime.label === 'Risk-OFF' ? '#dc2626' : '#6b7280',
                color: '#fff',
              }}>{globalCtx.regime.label}</span>
              <span style={{ color: '#888', fontSize: '0.7rem' }}>
                India bias: <b style={{ color: globalCtx.india_bias === 'bullish' ? '#16a34a' : globalCtx.india_bias === 'bearish' ? '#dc2626' : '#9ca3af' }}>
                  {String(globalCtx.india_bias || 'neutral').toUpperCase()}</b>
              </span>
              <span style={{ color: '#666', fontSize: '0.65rem' }}>{showGlobal ? '▲' : '▼'}</span>
            </span>
          </div>
          <div style={{ fontSize: '0.72rem', color: '#999', padding: '2px 2px 6px' }}>
            Global equities {Number(globalCtx.regime.equity_avg_pct || 0).toFixed(2)}% ·
            VIX {globalCtx.regime.vix ?? '—'} ·
            Crude {Number(globalCtx.regime.crude_pct || 0).toFixed(2)}% ·
            Gold {Number(globalCtx.regime.gold_pct || 0).toFixed(2)}% ·
            USD/INR {Number(globalCtx.regime.usdinr_pct || 0).toFixed(2)}%
            <span style={{ marginLeft: 8, opacity: 0.7 }}>updated {globalCtx.time}</span>
          </div>
          {showGlobal && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 6, marginTop: 6 }}>
              {(globalCtx.instruments || []).map(it => (
                <div key={it.ticker} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '6px 8px', borderRadius: 6, fontSize: '0.72rem',
                  background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)',
                }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: '1 1 auto' }} title={`${it.name} (${it.region})`}>{it.name}</span>
                  <span style={{ display: 'flex', gap: '0.4rem', alignItems: 'baseline', flex: '0 0 auto' }}>
                    <span style={{ color: '#cfd3dc' }}>{Number(it.price || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                    <span style={{ fontWeight: 700, minWidth: 54, textAlign: 'right', color: (it.change_pct || 0) >= 0 ? '#16a34a' : '#dc2626' }}>
                      {(it.change_pct || 0) >= 0 ? '+' : ''}{Number(it.change_pct || 0).toFixed(2)}%
                    </span>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {exitWin && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={() => !exitBusy && setExitWin(null)}>
          <div className="card" style={{ maxWidth: 360, width: '100%', border: '1px solid #ff4444' }} onClick={e => e.stopPropagation()}>
            <div style={{ fontWeight: 700, color: '#ff6b6b', marginBottom: 6 }}>Exit {exitWin.label}</div>
            <div style={{ fontSize: '0.8rem', color: '#aaa', marginBottom: 8 }}>Qty {exitWin.qty} · now ₹{exitWin.ltp ? exitWin.ltp.toFixed(2) : '—'}{exitWin.mode ? ` · ${exitWin.mode}` : ''}</div>
            <label style={{ display: 'block', fontSize: '0.85rem', color: '#ccc', marginBottom: 8 }}>
              Quantity to exit:{' '}
              {exitWin.lot ? (
                // QUANTITY IN LOT STEPS (owner 2026-10-23): lot 500 -> 500, 1000, 1500 ...
                <input type="number" min={exitWin.lot} max={exitWin.qty} step={exitWin.lot} value={exitN * exitWin.lot}
                  onChange={e => setExitN(Math.max(1, Math.min(Math.round(exitWin.qty / exitWin.lot), Math.round((parseInt(e.target.value, 10) || exitWin.lot) / exitWin.lot))))} style={{ width: 100 }} />
              ) : (
                <input type="number" min="1" max={exitWin.qty} value={exitN}
                  onChange={e => setExitN(Math.max(1, Math.min(exitWin.qty, parseInt(e.target.value, 10) || 1)))} style={{ width: 90 }} />
              )}
              <span style={{ color: '#888' }}> of {exitWin.qty}{exitWin.lot ? ` = ${exitN} of ${Math.round(exitWin.qty / exitWin.lot)} lots (${exitWin.lot} per lot)` : ''}</span>
              <button className="btn" style={{ marginLeft: 6, padding: '0.1rem 0.45rem', fontSize: '0.7rem' }}
                onClick={() => setExitN(exitWin.lot ? Math.round(exitWin.qty / exitWin.lot) : exitWin.qty)}>All</button>
            </label>
            <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
              {['MARKET', 'LIMIT'].map(t => (
                <button key={t} className="btn" onClick={() => setExitType(t)}
                  style={{ flex: 1, padding: '0.35rem', border: `1px solid ${exitType === t ? '#00c46a' : '#444'}`, color: exitType === t ? '#00ff88' : '#aaa', background: 'transparent' }}>
                  {t === 'MARKET' ? 'Market (now)' : 'Limit (my price)'}</button>))}
            </div>
            {exitType === 'LIMIT' ? (
              <label style={{ display: 'block', fontSize: '0.85rem', color: '#ccc', marginBottom: 8 }}>
                Sell price ₹ <input type="number" step="0.05" min="0" value={exitPx} autoFocus onChange={e => setExitPx(e.target.value)} style={{ width: 110 }} />
                {pending.some(o => o.pos_key === exitWin.key && o.kind === 'exit') && (
                  <div style={{ fontSize: '0.72rem', color: '#00ff88', marginTop: 4 }}>You already have a limit sell here — saving changes its price / quantity.</div>)}
                <div style={{ fontSize: '0.72rem', color: '#ffb020', marginTop: 4 }}>Waits until the price reaches ₹{exitPx || '…'}; the AI stop stays active meanwhile. Cancel it under Pending orders.</div>
              </label>
            ) : (
              <div style={{ fontSize: '0.75rem', color: '#888', marginBottom: 8 }}>Sells immediately at the market price{exitWin.mode === 'live' ? ' (Fyers market order with price protection)' : ''}.</div>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-stop" disabled={exitBusy || (exitType === 'LIMIT' && !(parseFloat(exitPx) > 0))} onClick={confirmExit}>
                {exitBusy ? 'Sending…' : exitType === 'LIMIT' ? (pending.some(o => o.pos_key === exitWin.key && o.kind === 'exit') ? 'Change limit exit' : 'Place limit exit') : 'Exit now'}</button>
              <button className="btn" disabled={exitBusy} onClick={() => setExitWin(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      {/* Single authoritative P&L from the backend — includes open positions,
          so it always matches the positions/trades shown below. */}
      <div className="card">
        <div className="card-header"><span className="card-title">Today's P&L</span><span style={{ color: '#666', fontSize: '0.65rem' }}>incl. open positions</span></div>
        <div className={`card-value ${(pnl.daily_pnl || 0) >= 0 ? 'positive' : 'negative'}`}>₹{Number(pnl.daily_pnl || 0).toFixed(2)}</div>
      </div>
      <div className="card" style={{ cursor: 'pointer' }} onClick={() => setShowMonths(v => !v)} title="Click for month-wise P&L">
        <div className="card-header">
          <span className="card-title">Total P&L ▾</span>
          <span style={{ color: '#666', fontSize: '0.65rem' }}>all-time · click for months</span>
        </div>
        <div className={`card-value ${(pnl.total_pnl || 0) >= 0 ? 'positive' : 'negative'}`}>₹{Number(pnl.total_pnl || 0).toFixed(2)}</div>
        {showMonths && (
          <div style={{ marginTop: '0.5rem', borderTop: '1px solid #333', paddingTop: '0.4rem' }}>
            {(pnl.month_wise && pnl.month_wise.length > 0) ? pnl.month_wise.map(m => (
              <div key={m.month} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', padding: '2px 0' }}>
                <span style={{ color: '#aaa' }}>{m.month} <span style={{ color: '#666' }}>({m.days}d)</span></span>
                <span className={(m.pnl || 0) >= 0 ? 'positive' : 'negative'}>₹{Number(m.pnl || 0).toFixed(2)}</span>
              </div>
            )) : <div style={{ color: '#888', fontSize: '0.75rem' }}>No history yet</div>}
          </div>
        )}
      </div>

      <div className="card">
        <div className="card-header"><span className="card-title">Positions</span></div>
        <div className="card-value neutral">{posEntriesForSegment().length}</div>
      </div>

      {/* Main Content with Activity Log */}
      <div className="main-activity-row" style={{ display: 'flex', gap: '1rem', gridColumn: '1 / -1' }}>
        {/* Left Side - Positions & Trades */}
        <div className="main-col" style={{ flex: 3 }}>
          <div className="card" style={{ marginBottom: '1rem' }}>
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span className="card-title">Open Positions</span>
              {closeMsg && <span style={{ fontSize: '0.75rem', color: closeMsg.t === 'success' ? '#00ff88' : '#ff6b6b', marginLeft: 8 }}>{closeMsg.m}</span>}
              {posEntriesForSegment().length > 0 && (
                <button className="btn btn-stop" style={{ fontSize: '0.7rem', padding: '0.2rem 0.6rem' }} onClick={async () => {
                  if (armClose !== '__all__') { setArmClose('__all__'); setTimeout(() => setArmClose(a => (a === '__all__' ? '' : a)), 4000); return; }
                  setArmClose('');
                  setCloseMsg({ t: 'success', m: 'Closing all open positions…' });
                  for (const [k] of posEntriesForSegment()) { try { await api.closePosition(k); } catch (e) {} }
                  setCloseMsg({ t: 'success', m: 'Close sent for all positions' });
                  fetchData();
                  setTimeout(() => setCloseMsg(null), 8000);
                }}>{armClose === '__all__' ? 'Confirm close ALL?' : 'Close All'}</button>
              )}
            </div>
            {posEntriesForSegment().length > 0 ? (
              <table className="positions-table dash-positions">
                <thead><tr><th>Symbol</th><th>Qty</th><th>Entry</th><th>LTP</th><th>P&amp;L</th><th>SL</th><th>Target</th><th title="Check to HOLD — AI will not exit, only suggest">Hold</th><th></th></tr></thead>
                <tbody>
                  {posEntriesForSegment().map(([symbol, pos]) => (
                    <tr key={symbol} style={pos.status === 'PENDING' ? { opacity: 0.75 } : undefined}>
                      <td>
                        {/* full CONTRACT (strike + CE/PE) on all segments, not just the underlying */}
                        <strong>{pos.trade_symbol || symbol}</strong>
                        <button className="chart-ico" title="Open chart" onClick={() => openChart(pos.trade_symbol || String(symbol).split('#')[0],
                          ['commodity', 'mcx_options'].includes(pos.segment) ? 'MCX' : 'NSE')}>📈</button>
                        {/* tags on their OWN line under the contract, solid and readable (owner 2026-10-23: '850 PE / OPTIONS not clearly seen') */}
                        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 4 }}>
                          {(() => {
                            const m = String(pos.trade_symbol || '').match(/(\d+(?:\.\d+)?)(CE|PE)$/);
                            return m ? (
                              <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#0a0a0f', background: m[2] === 'CE' ? '#00d26a' : '#ff5c5c', borderRadius: 4, padding: '1px 7px' }}>
                                {m[1]} {m[2]}
                              </span>
                            ) : null;
                          })()}
                          {pos.status === 'PENDING' && (
                            <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#0a0a0f', background: '#ffb020', borderRadius: 4, padding: '1px 7px' }}>PENDING</span>
                          )}
                          {pos.segment && (
                            <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#ffffff', background: '#4b4bd6', borderRadius: 4, padding: '1px 7px' }}>{String(pos.segment).toUpperCase()}</span>
                          )}
                          {pos.manual_hold && (
                            <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#0a0a0f', background: '#ffd700', borderRadius: 4, padding: '1px 7px' }}>ON HOLD</span>
                          )}
                        </div>
                        {(pos.btst || String(symbol).includes('@btst')) && (
                          <div style={{ marginTop: 3 }}>
                            <span style={{ fontSize: '0.62rem', fontWeight: 700, color: '#0a0a0f', background: '#ff9f1c', borderRadius: 4, padding: '1px 6px' }}
                                  title="Buy Today Sell Tomorrow — carried overnight on purpose; the bot sells it next morning">
                              BTST {pos.btst_opt ? 'OPTION' : `STOCK${pos.client_product ? ' ' + pos.client_product : ''}`}
                            </span>
                            <span style={{ fontSize: '0.62rem', color: '#ff9f1c', marginLeft: 6 }}>
                              {pos.carry_date && pos.carry_date === new Date().toLocaleDateString('en-CA')
                                ? 'bought today · sells tomorrow by 10:00 (or today before 15:28 at target/stop)'
                                : 'carried overnight · sells today by 10:00-10:30'}
                            </span>
                          </div>
                        )}
                        {pos.eq_product && (
                          <div style={{ fontSize: '0.65rem', color: '#ffb020', marginTop: 2 }}
                               title="Real cash blocked by this trade, the leverage behind it, and the full position value">
                            {pos.eq_product}{pos.eq_leverage > 1 ? ` ${Number(pos.eq_leverage).toFixed(1)}x` : ''}
                            {' · cash ₹'}{Number(pos.eq_margin || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                            {' · value ₹'}{Number(pos.eq_value || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                            {pos.swing ? ' · multi-day' : (pos.btst ? ' · BTST' : (pos.eq_product === 'MIS' ? ' · closes 15:10' : ''))}
                          </div>
                        )}
                      </td>
                      <td>{pos.quantity}</td>
                      <td>₹{pos.entry_price?.toFixed(2)}{pos.status === 'PENDING' ? ' (est)' : ''}</td>
                      <td style={{ color: '#00d9ff' }}>₹{pos.current_price?.toFixed(2) || '-'}</td>
                      <td className={pos.unrealized_pnl >= 0 ? 'positive' : 'negative'}>
                        {pos.unrealized_pnl >= 0 ? '+' : ''}₹{pos.unrealized_pnl?.toFixed(2) || '0.00'}
                      </td>
                      <td style={{ color: '#ff4444' }}>₹{pos.stop_loss?.toFixed(2)}
                        {pos.rk_stop_px ? <div style={{ fontSize: '0.68rem', color: '#ffb020' }} title={`Renko stop: exits when the stock makes a brick against the trade (brick Rs.${pos.rk_brick})`}>
                          Renko: stock {pos.trade_symbol?.endsWith('PE') ? '>' : '<'} ₹{(+pos.rk_stop_px).toFixed(2)}</div> : null}
                      </td>
                      <td style={{ color: '#00ff88' }}>₹{pos.take_profit?.toFixed(2)}</td>
                      <td style={{ textAlign: 'center' }}>
                        {(() => {
                          // show the pending checkbox state if the user just toggled it,
                          // else the saved backend value (so the 2s refresh can't revert it)
                          const cur = (symbol in holdPending) ? holdPending[symbol] : !!pos.manual_hold;
                          const dirty = (symbol in holdPending) && holdPending[symbol] !== !!pos.manual_hold;
                          return (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'center' }}>
                              <input type="checkbox" title="Hold — AI will NOT exit this position at any cost, only suggest"
                                checked={cur}
                                onChange={(e) => setHoldPending(p => ({ ...p, [symbol]: e.target.checked }))} />
                              <button
                                title="Save the Hold setting"
                                disabled={!dirty}
                                style={{ fontSize: '0.6rem', padding: '0.1rem 0.4rem', cursor: dirty ? 'pointer' : 'default',
                                         background: dirty ? '#2d6cdf' : '#333', color: '#fff', border: 'none', borderRadius: 3 }}
                                onClick={async () => {
                                  try {
                                    await api.holdPosition(symbol, cur);
                                    setHoldSaved(s => ({ ...s, [symbol]: Date.now() }));
                                    setHoldPending(p => { const n = { ...p }; delete n[symbol]; return n; });
                                    fetchData();
                                  } catch (er) { setError('Could not save Hold'); }
                                }}>Save</button>
                              {holdSaved[symbol] && Date.now() - holdSaved[symbol] < 4000 && (
                                <span style={{ fontSize: '0.6rem', color: '#00ff88' }}>✓</span>
                              )}
                            </div>
                          );
                        })()}
                        {(((symbol in holdPending) ? holdPending[symbol] : pos.manual_hold)) && pos.ai_suggestion && (
                          <div style={{ fontSize: '0.6rem', color: '#ffb020', marginTop: '2px', maxWidth: '150px' }}>💡 {pos.ai_suggestion}</div>
                        )}
                      </td>
                      <td>
                        <button className="btn btn-stop" style={{ fontSize: '0.65rem', padding: '0.2rem 0.6rem' }}
                          onClick={() => openExit(symbol, pos)}>Close</button>
                        {pending.some(o => o.pos_key === symbol && o.kind === 'exit') && (
                          <div style={{ fontSize: '0.6rem', color: '#ffb020', marginTop: 2, cursor: 'pointer', textDecoration: 'underline' }}
                            title="Edit this limit sell" onClick={() => openEdit(pending.find(o => o.pos_key === symbol && o.kind === 'exit'))}>
                            limit sell @ ₹{(pending.find(o => o.pos_key === symbol && o.kind === 'exit') || {}).limit} ✎</div>)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <p style={{ color: '#888', textAlign: 'center', padding: '1rem', fontSize: '0.9rem' }}>No open positions in {String(currentSegment).toUpperCase()}</p>}
            {pending.length > 0 && (
              <div style={{ marginTop: '0.6rem', borderTop: '1px solid #2a2a4a', paddingTop: '0.4rem' }}>
                <div style={{ color: '#ffb020', fontWeight: 700, fontSize: '0.8rem', marginBottom: 4 }}>Pending limit orders ({pending.length})</div>
                {pending.map(o => (
                  <div key={o.id} style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', flexWrap: 'wrap', fontSize: '0.75rem', padding: '2px 0' }}>
                    <b style={{ color: o.kind === 'entry' ? '#00ff88' : '#ff6b6b' }}>{o.kind === 'entry' ? 'BUY' : 'SELL'}</b>
                    <span>{o.trade_symbol} ×{o.qty}</span>
                    <span>@ ₹{o.limit}</span>
                    <span style={{ color: '#888' }}>({o.mode}{isAdmin && o.uid ? ` · ${o.uid}` : ''}, placed at ₹{o.ltp_at_order})</span>
                    <button className="btn" style={{ fontSize: '0.6rem', padding: '0.1rem 0.4rem' }} onClick={() => openEdit(o)}>Edit</button>
                    <button className="btn" style={{ fontSize: '0.6rem', padding: '0.1rem 0.4rem' }} onClick={() => cancelPending(o.id)}>Cancel</button>
                    {editPend && editPend.id === o.id && (
                      <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap', width: '100%', padding: '4px 0 4px 1rem' }}>
                        <span>Price ₹</span>
                        <input type="number" step="0.05" min="0" value={editPend.px} style={{ width: 80 }}
                          onChange={e => setEditPend({ ...editPend, px: e.target.value })} />
                        <span>{editPend.lot ? 'Quantity' : 'Qty'}</span>
                        <input type="number" min={editPend.lot || 1} step={editPend.lot || 1}
                          value={(editPend.lot || 1) * (parseInt(editPend.n, 10) || 0)} style={{ width: 80 }}
                          onChange={e => { const v = parseInt(e.target.value, 10) || 0; const l = editPend.lot || 1; setEditPend({ ...editPend, n: Math.max(1, Math.round(v / l)) }); }} />
                        {editPend.lot > 0 && <span style={{ color: '#888' }}>{editPend.n} lot{editPend.n > 1 ? 's' : ''} × {editPend.lot}</span>}
                        <button className="btn btn-start" style={{ fontSize: '0.6rem', padding: '0.1rem 0.5rem' }} onClick={saveEdit}>Save</button>
                        <button className="btn" style={{ fontSize: '0.6rem', padding: '0.1rem 0.4rem' }} onClick={() => setEditPend(null)}>✕</button>
                        {o.mode === 'live' && <span style={{ color: '#888', fontSize: '0.65rem' }}>live: the old order is cancelled and a new one placed at your broker</span>}
                      </div>)}
                  </div>))}
              </div>
            )}
            {otherSegPositions().length > 0 && (
              <p style={{ color: '#ffb020', textAlign: 'center', padding: '0.3rem', fontSize: '0.72rem' }}>
                ℹ️ {otherSegPositions().length} open position(s) in other segment(s) — switch that segment tab to view/manage.
              </p>
            )}
          </div>


          <div className="card">
            <div className="card-header">
              <span className="card-title">Today's Trades</span>
              <span style={{ color: '#888', fontSize: '0.75rem' }}>{trades.length} events</span>
            </div>
            <div className="trades-list" style={{ maxHeight: '320px', overflowY: 'auto' }}>
              {trades.length > 0 ? (
                trades.slice().sort((a, b) => String(b.timestamp || '').localeCompare(String(a.timestamp || ''))).map((trade, idx) => (
                  <div key={idx} className="trade-item">
                    <div>
                      <span style={{ color: '#555', fontSize: '0.7rem', marginRight: '0.4rem' }}>{String(trade.timestamp || '').slice(11, 19)}</span>
                      <strong>{trade.symbol}</strong>
                      <button className="chart-ico" title="Open chart" onClick={() => openChart(trade.symbol,
                        ['commodity', 'mcx_options'].includes(trade.segment) ? 'MCX' : 'NSE')}>📈</button>
                      <span className={`signal-badge signal-${trade.action?.toLowerCase()}`} style={{ marginLeft: '0.5rem', fontSize: '0.7rem' }}>{trade.action}</span>
                      {(trade.strategy === 'btst' || /BTST/i.test(String(trade.reason || ''))) && (
                        <span style={{ marginLeft: '0.3rem', fontSize: '0.62rem', fontWeight: 700, color: '#0a0a0f', background: '#ff9f1c', borderRadius: 4, padding: '1px 6px' }}>BTST</span>
                      )}
                      {trade.reason && <span className="signal-badge signal-hold" style={{ marginLeft: '0.3rem', fontSize: '0.65rem' }}>{trade.reason}</span>}
                      {trade.eq_product && (
                        <span style={{ marginLeft: '0.4rem', fontSize: '0.65rem', color: '#ffb020' }}
                              title="Real cash used and leverage on this stock trade">
                          {trade.eq_product}{trade.eq_leverage > 1 ? ` ${Number(trade.eq_leverage).toFixed(1)}x` : ''}
                          {' · cash ₹'}{Number(trade.eq_margin || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                          {' · value ₹'}{Number(trade.eq_value || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                        </span>
                      )}
                    </div>
                    <div>
                      <span style={{ fontSize: '0.85rem' }}>₹{trade.price?.toFixed(2)}</span>
                      {trade.ltp > 0 && (
                        <span style={{ marginLeft: '0.35rem', fontSize: '0.75rem', color: trade.ltp >= trade.price ? '#00ff88' : '#ff4444' }}>
                          LTP ₹{trade.ltp.toFixed(2)}
                        </span>
                      )}
                      {trade.pnl !== undefined && <span className={trade.pnl >= 0 ? 'positive' : 'negative'} style={{ marginLeft: '0.3rem', fontSize: '0.85rem' }}>{trade.pnl >= 0 ? '+' : ''}₹{trade.pnl?.toFixed(2)}</span>}
                    </div>
                  </div>
                ))
              ) : <p style={{ color: '#888', textAlign: 'center', padding: '1rem', fontSize: '0.9rem' }}>No trades yet</p>}
            </div>
          </div>
        </div>

        {/* Right Side - Activity Log */}
        <div className="activity-col" style={{ flex: 1 }}>
          <div className="card" style={{ height: '100%' }}>
            <div className="card-header">
              <span className="card-title" style={{ fontSize: '0.85rem' }}>Activity</span>
              {status?.is_running && <span className="signal-badge signal-buy" style={{ fontSize: '0.65rem' }}>LIVE</span>}
            </div>
            {/* Activity Tabs */}
            <div style={{ display: 'flex', gap: '0.3rem', marginBottom: '0.5rem', borderBottom: '1px solid #1a1a2e', paddingBottom: '0.4rem' }}>
              {['log', 'stats', 'trades', 'orders'].map((tab) => (
                <button key={tab} onClick={() => setActivityTab(tab)}
                  style={{ padding: '0.2rem 0.5rem', fontSize: '0.65rem', borderRadius: '4px', border: 'none', cursor: 'pointer',
                    background: activityTab === tab ? '#00d9ff22' : 'transparent', color: activityTab === tab ? '#00d9ff' : '#666' }}>
                  {tab === 'log' ? 'Log' : tab === 'stats' ? 'Stats' : tab === 'trades' ? 'Trades' : 'Orders'}
                </button>
              ))}
            </div>
            <div style={{ maxHeight: '350px', overflowY: 'auto', fontSize: '0.7rem' }}>
              {/* Log Tab */}
              {activityTab === 'log' && (
                activityData?.engine_log?.length > 0 ? (
                  activityData.engine_log.slice().reverse().map((log, idx) => {
                    const colors = { trade: '#00ff88', signal: '#00d9ff', error: '#ff4444', scan: '#888', market: '#f59e0b', system: '#a855f7' };
                    return (
                      <div key={idx} style={{ padding: '0.25rem 0', borderBottom: '1px solid #1a1a2e', color: colors[log.category] || '#888' }}>
                        <span style={{ color: '#444', fontSize: '0.6rem' }}>{log.time}</span>{' '}
                        <span style={{ fontSize: '0.6rem', color: '#555' }}>[{log.category}]</span>{' '}
                        {log.message}
                      </div>
                    );
                  })
                ) : (
                  <p style={{ color: '#555', textAlign: 'center', padding: '1rem' }}>Start trading to see activity</p>
                )
              )}
              {/* Stats Tab */}
              {activityTab === 'stats' && activityData?.stats && (
                <div style={{ padding: '0.3rem' }}>
                  {Object.entries(activityData.stats).map(([key, val]) => (
                    <div key={key} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.2rem 0', borderBottom: '1px solid #1a1a2e' }}>
                      <span style={{ color: '#888' }}>{key.replace(/_/g, ' ')}</span>
                      <span style={{ color: '#00d9ff' }}>{typeof val === 'object' ? JSON.stringify(val) : val}</span>
                    </div>
                  ))}
                </div>
              )}
              {/* Trades Tab */}
              {activityTab === 'trades' && (
                activityData?.recent_trades?.length > 0 ? (
                  activityData.recent_trades.slice().sort((a, b) => String(b.time || b.timestamp || '').localeCompare(String(a.time || a.timestamp || ''))).map((t, idx) => (
                    <div key={idx} style={{ padding: '0.25rem 0', borderBottom: '1px solid #1a1a2e' }}>
                      <span style={{ color: t.action === 'BUY' ? '#00ff88' : '#ff4444', fontWeight: 600 }}>{t.action}</span>{' '}
                      <strong>{t.symbol}</strong>{' '}
                      <span style={{ color: '#888' }}>@ ₹{t.price?.toFixed(2)}</span>
                      {t.ltp > 0 && <span style={{ marginLeft: '0.3rem', color: t.ltp >= t.price ? '#00ff88' : '#ff4444' }}>LTP ₹{t.ltp.toFixed(2)}</span>}
                      {t.pnl !== undefined && <span className={t.pnl >= 0 ? 'positive' : 'negative'} style={{ marginLeft: '0.3rem' }}>{t.pnl >= 0 ? '+' : ''}₹{t.pnl?.toFixed(2)}</span>}
                      {t.reason && <span style={{ color: '#555', marginLeft: '0.3rem', fontSize: '0.6rem' }}>({t.reason})</span>}
                    </div>
                  ))
                ) : (
                  <p style={{ color: '#555', textAlign: 'center', padding: '1rem' }}>No trades yet</p>
                )
              )}
              {/* Orders Tab */}
              {activityTab === 'orders' && (
                activityData?.orders?.length > 0 ? (
                  <div>
                    <div style={{ fontSize: '0.6rem', color: '#888', marginBottom: '0.3rem' }}>Your orders — <span style={{ color: '#00ff88' }}>COMPLETED</span> or <span style={{ color: '#ff4444' }}>FAILED</span> (skipped signals + reasons are in the Log tab)</div>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.65rem' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid #1a1a2e', color: '#888' }}>
                          <th style={{ padding: '0.2rem 0.3rem', textAlign: 'left' }}>Time</th>
                          <th style={{ padding: '0.2rem 0.3rem', textAlign: 'left' }}>Type</th>
                          <th style={{ padding: '0.2rem 0.3rem', textAlign: 'left' }}>Symbol</th>
                          <th style={{ padding: '0.2rem 0.3rem', textAlign: 'right' }}>Qty</th>
                          <th style={{ padding: '0.2rem 0.3rem', textAlign: 'right' }}>Price</th>
                          <th style={{ padding: '0.2rem 0.3rem', textAlign: 'center' }}>Status</th>
                          <th style={{ padding: '0.2rem 0.3rem', textAlign: 'left' }}>Msg</th>
                        </tr>
                      </thead>
                      <tbody>
                        {activityData.orders.slice().reverse().map((o, idx) => (
                          <React.Fragment key={idx}>
                          <tr style={{ borderBottom: o.status === 'FAILED' ? 'none' : '1px solid #1a1a2e' }}>
                            <td style={{ padding: '0.2rem 0.3rem', color: '#555' }}>{o.time}</td>
                            <td style={{ padding: '0.2rem 0.3rem', color: (o.type === 'BUY' || o.type === 'OPEN') ? '#00ff88' : o.type === 'SELL' ? '#ff8844' : '#888' }}>{o.type}</td>
                            <td style={{ padding: '0.2rem 0.3rem' }}><strong>{o.symbol}</strong>
                              {o.source === 'manual'
                                ? <span style={{ marginLeft: '0.3rem', fontSize: '0.55rem', padding: '0 0.25rem', borderRadius: 3, background: 'rgba(255,176,32,0.18)', color: '#ffb020' }}>YOUR MANUAL</span>
                                : o.source === 'system' ? <span style={{ marginLeft: '0.3rem', fontSize: '0.55rem', padding: '0 0.25rem', borderRadius: 3, background: 'rgba(0,217,255,0.12)', color: '#00d9ff' }}>SYSTEM</span> : null}
                            </td>
                            <td style={{ padding: '0.2rem 0.3rem', textAlign: 'right' }}>{o.quantity ?? '-'}</td>
                            <td style={{ padding: '0.2rem 0.3rem', textAlign: 'right' }}>{o.price != null ? `₹${Number(o.price).toFixed(2)}` : '-'}</td>
                            <td style={{ padding: '0.2rem 0.3rem', textAlign: 'center', fontWeight: 600, color: (o.status === 'COMPLETED' || o.status === 'FILLED' || o.status === 'success') ? '#00ff88' : '#ff4444' }}>{o.status}</td>
                            <td style={{ padding: '0.2rem 0.3rem', color: o.status === 'FAILED' ? '#ff8844' : '#aaa', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={o.message}>{o.message || '-'}</td>
                          </tr>
                          {o.status === 'FAILED' && (
                            <tr style={{ borderBottom: '1px solid #1a1a2e' }}>
                              <td colSpan={7} style={{ padding: '0.15rem 0.3rem 0.4rem', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                <div style={{ color: '#ff8080', fontWeight: 600 }}>❌ Why it failed: {o.why || 'broker rejected the order'}</div>
                                {o.broker_msg && <div style={{ color: '#888', fontSize: '0.6rem' }}>Broker said: {o.broker_msg}</div>}
                              </td>
                            </tr>
                          )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p style={{ color: '#555', textAlign: 'center', padding: '1rem' }}>No orders yet</p>
                )
              )}
            </div>
            {/* Market Snapshots */}
            {activityTab === 'log' && activityData?.market_snapshots?.length > 0 && (
              <div style={{ borderTop: '1px solid #1a1a2e', marginTop: '0.5rem', paddingTop: '0.4rem' }}>
                <div style={{ fontSize: '0.6rem', color: '#555', marginBottom: '0.3rem' }}>MARKET MOVERS</div>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {activityData.market_snapshots.map((m, i) => (
                    <span key={i} style={{ fontSize: '0.6rem', color: m.change_pct >= 0 ? '#00ff88' : '#ff4444' }}>
                      {m.symbol} ₹{m.price?.toFixed(0)} ({m.change_pct >= 0 ? '+' : ''}{m.change_pct}%)
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {error && <div className="error">{error}</div>}
    </div>
  );
}

export default Dashboard;
